/**
 * Community Garden — Avatar Store (GARDEN-142)
 * 
 * Single source of truth for avatar state.
 * Centralizes avatar data that was previously scattered across auth service
 * and avatar customization components.
 */

import type { AvatarEquipped } from '../avatar-catalog.js';

/* ── Types ───────────────────────────────────────────── */

export interface AvatarState {
  /** The equipped avatar customization options */
  equipped: AvatarEquipped | null;
  /** The Discord avatar URL (if any) */
  discordAvatarUrl: string | null;
  /** User ID associated with this avatar state */
  userId: string | null;
  /** Whether the avatar state has been initialized */
  isInitialized: boolean;
  /** Last update timestamp */
  lastUpdated: number;
}

export type AvatarSubscriber = (state: AvatarState, previousState: AvatarState) => void;

export interface AvatarChangeEvent {
  type: 'equipped' | 'discord' | 'full';
  equipped: AvatarEquipped | null;
  discordAvatarUrl: string | null;
  userId: string | null;
  timestamp: number;
}

/* ── Storage Keys ────────────────────────────────────── */

const STORAGE_KEY = 'garden:avatar:v1';

/* ── Avatar Store ────────────────────────────────────── */

class AvatarStore {
  private state: AvatarState = {
    equipped: null,
    discordAvatarUrl: null,
    userId: null,
    isInitialized: false,
    lastUpdated: 0,
  };

  private subscribers: Set<AvatarSubscriber> = new Set();
  private eventListeners: Set<(event: AvatarChangeEvent) => void> = new Set();

  constructor() {
    // Load from storage on initialization
    this.loadFromStorage();
  }

  /* ── Getters ───────────────────────────────────────── */

  getState(): AvatarState {
    return { ...this.state };
  }

  getEquipped(): AvatarEquipped | null {
    return this.state.equipped ? { ...this.state.equipped } : null;
  }

  getDiscordAvatarUrl(): string | null {
    return this.state.discordAvatarUrl;
  }

  getUserId(): string | null {
    return this.state.userId;
  }

  isReady(): boolean {
    return this.state.isInitialized;
  }

  /* ── Setters ───────────────────────────────────────── */

  /**
   * Set the full equipped avatar state
   */
  setEquipped(equipped: AvatarEquipped | null): void {
    const previousState = this.getState();
    
    this.state = {
      ...this.state,
      equipped: equipped ? { ...equipped } : null,
      lastUpdated: Date.now(),
      isInitialized: true,
    };

    this.persistAndNotify(previousState, 'equipped');
  }

  /**
   * Set the Discord avatar URL
   */
  setDiscordAvatarUrl(url: string | null): void {
    const previousState = this.getState();
    
    this.state = {
      ...this.state,
      discordAvatarUrl: url,
      lastUpdated: Date.now(),
    };

    this.persistAndNotify(previousState, 'discord');
  }

  /**
   * Set the user ID associated with this avatar
   */
  setUserId(userId: string | null): void {
    const previousState = this.getState();
    
    this.state = {
      ...this.state,
      userId,
      lastUpdated: Date.now(),
    };

    this.persistAndNotify(previousState, 'full');
  }

  /**
   * Initialize the full avatar state at once
   */
  initialize(userId: string, equipped: AvatarEquipped | null, discordAvatarUrl: string | null): void {
    const previousState = this.getState();
    
    this.state = {
      equipped: equipped ? { ...equipped } : null,
      discordAvatarUrl,
      userId,
      isInitialized: true,
      lastUpdated: Date.now(),
    };

    this.persistAndNotify(previousState, 'full');
  }

  /**
   * Update equipped avatar from individual components
   */
  updateEquipped(updates: Partial<AvatarEquipped>): void {
    const previousState = this.getState();
    
    this.state = {
      ...this.state,
      equipped: this.state.equipped 
        ? { ...this.state.equipped, ...updates }
        : { 
            avatarSkinToneId: updates.avatarSkinToneId || 'skin_fair',
            avatarHairId: updates.avatarHairId || 'hair_black',
            avatarHairStyleId: updates.avatarHairStyleId ?? null,
            avatarTopColorId: updates.avatarTopColorId || 'top_blue',
            avatarBottomColorId: updates.avatarBottomColorId || 'bot_navy',
            equippedSpecialOutfitId: updates.equippedSpecialOutfitId ?? null,
          },
      lastUpdated: Date.now(),
      isInitialized: true,
    };

    this.persistAndNotify(previousState, 'equipped');
  }

  /**
   * Clear all avatar state (e.g., on logout)
   */
  reset(): void {
    const previousState = this.getState();
    
    this.state = {
      equipped: null,
      discordAvatarUrl: null,
      userId: null,
      isInitialized: false,
      lastUpdated: Date.now(),
    };

    this.clearStorage();
    this.notifySubscribers(previousState);
    this.dispatchEvent('full');
  }

  /* ── Subscriptions ─────────────────────────────────── */

  /**
   * Subscribe to avatar state changes
   * Returns unsubscribe function
   */
  subscribe(callback: AvatarSubscriber): () => void {
    this.subscribers.add(callback);
    
    // Immediately call with current state if initialized
    if (this.state.isInitialized) {
      callback(this.getState(), this.getState());
    }

    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Subscribe to avatar change events
   * Returns unsubscribe function
   */
  onAvatarChange(callback: (event: AvatarChangeEvent) => void): () => void {
    this.eventListeners.add(callback);
    return () => {
      this.eventListeners.delete(callback);
    };
  }

  /* ── Private Methods ───────────────────────────────── */

  private persistAndNotify(previousState: AvatarState, eventType: 'equipped' | 'discord' | 'full'): void {
    this.saveToStorage();
    this.notifySubscribers(previousState);
    this.dispatchEvent(eventType);
  }

  private notifySubscribers(previousState: AvatarState): void {
    const currentState = this.getState();
    this.subscribers.forEach(callback => {
      try {
        callback(currentState, previousState);
      } catch (error) {
        console.error('[AvatarStore] Subscriber error:', error);
      }
    });
  }

  private dispatchEvent(type: 'equipped' | 'discord' | 'full'): void {
    const event: AvatarChangeEvent = {
      type,
      equipped: this.state.equipped,
      discordAvatarUrl: this.state.discordAvatarUrl,
      userId: this.state.userId,
      timestamp: Date.now(),
    };

    // Notify event listeners
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[AvatarStore] Event listener error:', error);
      }
    });

    // Dispatch custom event for cross-component updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('garden:avatar:change', {
        detail: event
      }));
    }
  }

  /* ── Persistence ────────────────────────────────────── */

  private saveToStorage(): void {
    try {
      const data = JSON.stringify(this.state);
      localStorage.setItem(STORAGE_KEY, data);
    } catch {
      // Ignore storage errors (e.g., private browsing mode)
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only restore if userId matches current session
        // Full validation happens when user store initializes
        if (parsed.userId) {
          this.state = {
            ...parsed,
            isInitialized: false, // Require explicit initialization
          };
        }
      }
    } catch {
      // Ignore storage errors
    }
  }

  private clearStorage(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  }
}

/* ── Singleton Export ───────────────────────────────── */

export const avatarStore = new AvatarStore();
export { AvatarStore };
